import {
  ActivityStatus,
  ResidentActivityType,
  SubActivityType,
} from "../residentActivityPlan";
import { SortDirection } from "../types";

export type AppStateMonitorAlerts = {
  alerts: AlertMap;
  devicesAlerts: AlertMap;
  reminders: RemindersMap;
  isLoading: boolean;
  settings: StatusSettings;
};

export type StatusSettings = {
  view?: ViewType;
  presentation?: PresentationType;
};
export enum ViewType {
  Notification,
  Status,
}

export enum PresentationType {
  Cards,
  Tile,
  List,
}
export type AlertMap = {
  [key: string]: IAlertMonitor[];
};

export type RemindersMap = {
  [key: string]: Reminder[];
};
export enum StatusSorting {
  Room = "Room",
  Urgency = "Urgency",
}

export enum AlertSortType {
  Room = "Room",
  Urgency = "Urgency",
  Time = "Time",
}

export type AlertsSorting = {
  direction: SortDirection;
  type: AlertSortType;
};

export enum AlertStatus {
  open = "open",
  handling = "handling",
  done = "done",
  canceled = "canceled",
  updated = "updated",
  handlingUpdated = "handlingUpdated",
  reset = "reset",
  ManualDone = "manualDone",
  AutomaticallyDone = "automaticallyDone",
}

export enum AlertStatusSocket {
  open = 0,
  handling = 1,
  done = 2,
  canceled = 3,
  updated = 4,
  handlingUpdated = 5,
  reset = 6,
  Replaces = 7,
  ManualDone = 8,
  AutomaticallyDone = 9,
}

export enum AlertToken {
  BTAbove = "BTAbove",
  BTBelow = "BTBelow",
  SpO2Above = "SpO2Above",
  SpO2Below = "SpO2Below",
  HRAbove = "HRAbove",
  HRBelow = "HRBelow",
  RRAbove = "RRAbove",
  RRBelow = "RRBelow",
  TurnResident = "TurnResident",
  ResidentTurned = "ResidentTurned",
  Fall = "Fall",
  FallDetected = "FallDetected",
  FallLowHR = "FallLowHR",
  EpilepsyDetection = "EpilepsyDetection",
  PowerUnplugged = "PowerUnplugged",
  WrongDevicePlacement = "wrongDevicePlacement",
  NoWiFi = "NoWiFi",
  CallButton = "CallButton",
  BPAbove = "BPAbove",
  BPBelow = "BPBelow",
  InBed = "InBed",
  OutOfBed = "OutOfBed",
  AwayFromBed = "AwayFromBed",
  UserNearby = "UserNearby",
}
/**General monitor application group name, for example:
AppID: HRThresholds will include AlertTokens: HRAbove and HRBelow
Possible values: [BTThresholds, SpO2Thresholds, HRThresholds, RRThresholds, PressureUlcers, EpilepsyDetection] */
export enum AlertAppID {
  PressureUlcers = "PressureUlcers",
  Fall = "Fall",
  FallDetected = "FallDetected",
  HRThresholds = "HRThresholds",
  RRThresholds = "RRThresholds",
  BTThresholds = "BTThresholds",
  SpO2Thresholds = "SpO2Thresholds",
  BPThresholds = "BPThresholds",
  CallButton = "CallButton",
  InBed = "InBed",
  OutOfBed = "OutOfBed",
  AwayFromBed = "AwayFromBed",
  UserNearby = "UserNearby",
  EpilepsyDetection = "EpilepsyDetection",
  PowerUnplugged = "PowerUnplugged",
  WrongDevicePlacement = "WrongDevicePlacement",
  NoWiFi = "NoWiFi",
}
export enum AlertPriority {
  low = 1,
  med = 1.5,
  high = 2,
}

export type AlertStatusUpdate = {
  newStatus: AlertStatus;
  oldStatus: AlertStatus;
  reason?: string;
  updateTime: string;
};

export type Reminder = {
  activityType: ResidentActivityType;
  lastUpdate: string;
  activityParams?: SubActivityType[];
  nodeId: number;
  residentId: string;
  dailyScheduleId?: number;
  room?: string;
  bed?: string;
  utcDate: string;
};
export type ReminderAction = {
  activityType: ResidentActivityType;
  activityParams?: SubActivityType[];
  nodeId: number;
  residentId: string;
  activityTime: string;
  activityStatus: ActivityStatus;
  utcActivityTime: string;
};

export type Alert = {
  generated?: string;

  alertToken: AlertToken;
  // alertId: string;
  patientId: string;
  patientName: string;
  deviceId?: string;
  // id: AlertAppID;
  priority: AlertPriority;
  /**Last change of status time */
  time?: string;
  /**Alert creation date */
  date: string;
  title?: string;
  descriptionFormat?: string;
  description?: string;
  status: AlertStatus;
  statusHistory?: AlertStatusUpdate[];
  alertParams?: any;
  /**Last change of status time */
  lastUpdate: string;
  lastUpdateReason?: string;
  /**Time when the alert will resend if it continuously present */
  waitBeforeRepeat?: number;
  feedback?: string;
  response?: string;
  showPushNotification?: boolean;
  user?: UserNearbyTagInfo;
};

export type UserNearbyTagInfo = {
  lastName: string;
  firstName: string;
  picture: string;
  id: string;
};
export type AlertSocket = {
  alertToken: AlertToken;
  feedback: null;
  firstName: string;
  icon: string;
  lastName: string;
  date: string;
  lastUpdate: string;
  lastUpdateReason: null;
  nodeId: number;
  patientId: string;
  patientName: string;
  priority: AlertPriority;
  response: null;
  room: string;
  showPushNotification: boolean;
  status: AlertStatusSocket;
  statusHistory: AlertStatusUpdate[];
  time: string;
  title: string;
  typeId: string;
  waitBeforeRepeat: number;
  bed: string;
};

export interface IStatusMonitor {
  patientId: string;
  room: string;
  bed: string;
  patientName: string;
  nodeId?: number;
  alerts: Alert[];
}

export interface IAlertMonitor extends Alert {
  alertId?: string;
  room: string;
  bed: string;
  nodeId: number;
}

export type GetAlertDTO = {
  residentId: string;
  startFrom: Date;
};

export type GetAlertUpdateDTO = {
  status: AlertStatus;
  generated: string;
  lastUpdate: string;
  priority: AlertPriority;
};

export type AlertResponseResidents = GetAlertUpdateDTO;

export const fromAlertTokenToFilter = (token: AlertToken): string => {
  if (token === AlertToken.AwayFromBed) return AlertAppID.AwayFromBed + "Alert";
  if (token === AlertToken.InBed) return AlertAppID.InBed + "Alert";
  if (token === AlertToken.OutOfBed) return AlertAppID.OutOfBed + "Alert";
  return fromAlertTokenToAppId(token);
};

export const fromAlertTokenToAppId = (token: AlertToken): AlertAppID => {
  if (token === AlertToken.BTBelow || token === AlertToken.BTAbove)
    return AlertAppID.BTThresholds;
  if (token === AlertToken.SpO2Below) return AlertAppID.SpO2Thresholds;
  if (
    token === AlertToken.HRAbove ||
    token === AlertToken.FallLowHR ||
    token === AlertToken.HRBelow
  )
    return AlertAppID.HRThresholds;
  if (token === AlertToken.RRAbove || token === AlertToken.RRBelow)
    return AlertAppID.RRThresholds;
  if (token === AlertToken.TurnResident || token === AlertToken.ResidentTurned)
    return AlertAppID.PressureUlcers;
  if (token === AlertToken.EpilepsyDetection)
    return AlertAppID.EpilepsyDetection;
  if (token === AlertToken.Fall) return AlertAppID.Fall;
  if (token === AlertToken.FallDetected) return AlertAppID.FallDetected;
  if (token === AlertToken.PowerUnplugged) return AlertAppID.PowerUnplugged;
  if (token === AlertToken.WrongDevicePlacement)
    return AlertAppID.WrongDevicePlacement;
  if (token === AlertToken.NoWiFi) return AlertAppID.NoWiFi;
  if (token === AlertToken.BPAbove || token === AlertToken.BPBelow)
    return AlertAppID.BPThresholds;
  if (token === AlertToken.CallButton) return AlertAppID.CallButton;
  if (token === AlertToken.AwayFromBed) return AlertAppID.AwayFromBed;
  if (token === AlertToken.InBed) return AlertAppID.InBed;
  if (token === AlertToken.OutOfBed) return AlertAppID.OutOfBed;
  if (token === AlertToken.UserNearby) return AlertAppID.UserNearby;
};

export enum MonitorAlertsFilter {
  BTThresholds = "BTThresholds",
  SpO2Thresholds = "SpO2Thresholds",
  HRThresholds = "HRThresholds",
  RRThresholds = "RRThresholds",
  PressureUlcers = "PressureUlcers",
  EpilepsyDetection = "EpilepsyDetection",
  Fall = "Fall",
  FallDetected = "FallDetected",
  BPThresholds = "BPThresholds",
  CallButton = "CallButton",
  InBedAlert = "InBedAlert",
  OutOfBedAlert = "OutOfBedAlert",
  AwayFromBedAlert = "AwayFromBedAlert",
  PowerUnplugged = "powerUnplugged",
  WrongDevicePlacement = "WrongDevicePlacement",
  NoWiFi = "NoWiFi",
  Power = "power",
  BatteryLevelLow = "BatteryLevelLow",
  BatteryLevelEmpty = "BatteryLevelEmpty",
  Wifi = "wifi",
  Bluetooth = "bluetooth",
  InBed = "inBed",
  OutOfBed = "outOfBed",
  NoDevice = "NoDevice",
  Away = "Away",
  USER_NEARBY_IN_BED = "userNearbyInBed",
  USER_NEARBY_OUT_BED = "userNearbyOutBed",
  USER_NEARBY_ROOM = "userNearbyRoom",
  WatchDeath = "WatchDeath",
  UserNearby = "UserNearby",
  Device_Vitals = "Device_Vitals",
  Device_Biopatch = "Device_Biopatch",
  Device_Watch = "Device_Watch",
}

export type StatusFilterMap = {
  [key in MonitorAlertsFilter | ResidentActivityType]?: boolean;
};

export type AlertsFilterMap = {
  [key in AlertAppID]: boolean;
};

export enum AlertPriorityLevel {
  AWAY = 1,
  NO_NOTIFICATION = 2,
  YELLOW = 3,
  BLACK = 4,
  RED = 5,
}
const feedbackToken = [
  AlertToken.Fall,
  AlertToken.FallDetected,
  AlertToken.TurnResident,
  AlertToken.AwayFromBed,
  AlertToken.InBed,
  AlertToken.OutOfBed,
];
export const isHandledType = (token: AlertToken): boolean => {
  return feedbackToken.indexOf(token) !== -1;
};
const feedbackAppId = [
  AlertAppID.Fall,
  AlertAppID.FallDetected,
  AlertAppID.PressureUlcers,
  AlertAppID.AwayFromBed,
  AlertAppID.InBed,
  AlertAppID.OutOfBed,
];
export const isHandledTypeId = (id: AlertAppID): boolean => {
  return feedbackAppId.indexOf(id) !== -1;
};

export const EMPTY_STATUS_PAGE_FILTER = (): StatusFilterMap => {
  const filter = {
    [MonitorAlertsFilter.EpilepsyDetection]: false,
    [MonitorAlertsFilter.BTThresholds]: false,
    [MonitorAlertsFilter.Fall]: false,
    [MonitorAlertsFilter.FallDetected]: false,
    [MonitorAlertsFilter.HRThresholds]: false,
    [MonitorAlertsFilter.PressureUlcers]: false,
    [MonitorAlertsFilter.RRThresholds]: false,
    [MonitorAlertsFilter.SpO2Thresholds]: false,
    [MonitorAlertsFilter.BPThresholds]: false,
    [MonitorAlertsFilter.CallButton]: false,
    [MonitorAlertsFilter.OutOfBedAlert]: false,
    [MonitorAlertsFilter.InBedAlert]: false,
    [MonitorAlertsFilter.AwayFromBedAlert]: false,
    [MonitorAlertsFilter.UserNearby]: false,
  };
  Object.values(ResidentActivityType).map((el) => (filter[el] = false));
  [
    MonitorAlertsFilter.BatteryLevelLow,
    MonitorAlertsFilter.Wifi,
    MonitorAlertsFilter.Bluetooth,
    MonitorAlertsFilter.Power,
    MonitorAlertsFilter.OutOfBed,
    MonitorAlertsFilter.InBed,
    MonitorAlertsFilter.BatteryLevelEmpty,
    MonitorAlertsFilter.NoDevice,
    MonitorAlertsFilter.Away,
    MonitorAlertsFilter.USER_NEARBY_IN_BED,
    MonitorAlertsFilter.USER_NEARBY_OUT_BED,
    MonitorAlertsFilter.USER_NEARBY_ROOM,
    MonitorAlertsFilter.Device_Biopatch,
    MonitorAlertsFilter.Device_Watch,
    MonitorAlertsFilter.Device_Vitals,
  ].map((el) => (filter[el] = false));
  return filter;
};

export const EMPTY_ALERTS_PAGE_FILTER = (): StatusFilterMap => {
  const filter = {
    [MonitorAlertsFilter.EpilepsyDetection]: false,
    [MonitorAlertsFilter.BTThresholds]: false,
    [MonitorAlertsFilter.Fall]: false,
    [MonitorAlertsFilter.FallDetected]: false,
    [MonitorAlertsFilter.HRThresholds]: false,
    [MonitorAlertsFilter.PressureUlcers]: false,
    [MonitorAlertsFilter.RRThresholds]: false,
    [MonitorAlertsFilter.SpO2Thresholds]: false,
    [MonitorAlertsFilter.BPThresholds]: false,
    [MonitorAlertsFilter.CallButton]: false,
    [MonitorAlertsFilter.OutOfBedAlert]: false,
    [MonitorAlertsFilter.InBedAlert]: false,
    [MonitorAlertsFilter.AwayFromBedAlert]: false,
    [MonitorAlertsFilter.UserNearby]: false,
  };
  Object.values(ResidentActivityType).map((el) => (filter[el] = false));

  return filter;
};
